import { Controller, type UseFormReturn, useWatch } from "react-hook-form";

import { TimePicker } from "@/components/time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { ScheduleDayValues, TimeSettingsFormValues } from "../../types";
import {
	expectedMinutes,
	formatHours,
	WEEKDAY_LABELS,
	weeklyExpectedMinutes,
} from "../../utils/schedule";

const EMPTY_VALUE = "—";

function expectedLabel(day: ScheduleDayValues): string {
	const minutes = expectedMinutes(day);
	if (minutes == null) return EMPTY_VALUE;
	return formatHours(minutes);
}

export function ScheduleForm({
	form,
}: {
	form: UseFormReturn<TimeSettingsFormValues>;
}) {
	const {
		control,
		register,
		formState: { errors },
	} = form;
	const schedule = useWatch({ control, name: "schedule" });
	const weekly = weeklyExpectedMinutes(schedule);

	return (
		<div className="schedule-form">
			<div className="schedule-form__head" aria-hidden="true">
				<span>Dia</span>
				<span>Dia de trabalho</span>
				<span>Início</span>
				<span>Fim</span>
				<span>Intervalo (min)</span>
				<span>Horas esperadas</span>
			</div>

			{schedule.map((day, index) => {
				const label = WEEKDAY_LABELS[day.weekday] ?? "";
				const rowErrors = errors.schedule?.[index];
				const startId = `schedule-${day.weekday}-start`;
				const endId = `schedule-${day.weekday}-end`;
				const breakId = `schedule-${day.weekday}-break`;
				return (
					<div
						key={day.weekday}
						className="schedule-form__row"
						data-enabled={day.enabled ? "true" : "false"}
						data-weekday={day.weekday}
					>
						<p className="schedule-form__day">{label}</p>

						<div className="schedule-form__toggle">
							<Controller
								control={control}
								name={`schedule.${index}.enabled`}
								render={({ field }) => (
									<Switch
										id={`schedule-${day.weekday}-enabled`}
										checked={field.value}
										onCheckedChange={field.onChange}
										aria-label={`Dia de trabalho: ${label}`}
									/>
								)}
							/>
							<Label
								htmlFor={`schedule-${day.weekday}-enabled`}
								className="schedule-form__toggle-label"
							>
								Dia de trabalho
							</Label>
						</div>

						<div className="form-group">
							<Label htmlFor={startId} className="schedule-form__field-label">
								Início de {label}
							</Label>
							<Controller
								control={control}
								name={`schedule.${index}.startsAt`}
								render={({ field }) => (
									<TimePicker
										id={startId}
										disabled={!day.enabled}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										aria-invalid={!!rowErrors?.startsAt}
									/>
								)}
							/>
						</div>

						<div className="form-group">
							<Label htmlFor={endId} className="schedule-form__field-label">
								Fim de {label}
							</Label>
							<Controller
								control={control}
								name={`schedule.${index}.endsAt`}
								render={({ field }) => (
									<TimePicker
										id={endId}
										disabled={!day.enabled}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										aria-invalid={!!rowErrors?.endsAt}
									/>
								)}
							/>
						</div>

						<div className="form-group">
							<Label htmlFor={breakId} className="schedule-form__field-label">
								Intervalo de {label} (min)
							</Label>
							<Input
								id={breakId}
								inputMode="numeric"
								disabled={!day.enabled}
								aria-invalid={!!rowErrors?.breakMinutes}
								{...register(`schedule.${index}.breakMinutes`)}
							/>
						</div>

						<p
							className="schedule-form__expected"
							data-testid={`expected-${day.weekday}`}
						>
							<span className="schedule-form__expected-label">
								Horas esperadas
							</span>
							{expectedLabel(day)}
						</p>

						{rowErrors && (
							<div className="schedule-form__errors">
								{rowErrors.startsAt && (
									<p className="form-error">{rowErrors.startsAt.message}</p>
								)}
								{rowErrors.endsAt && (
									<p className="form-error">{rowErrors.endsAt.message}</p>
								)}
								{rowErrors.breakMinutes && (
									<p className="form-error">{rowErrors.breakMinutes.message}</p>
								)}
							</div>
						)}
					</div>
				);
			})}

			<div className="schedule-form__total">
				<span>Total semanal</span>
				<strong data-testid="expected-week">
					{weekly == null ? EMPTY_VALUE : formatHours(weekly)}
				</strong>
			</div>
			{errors.schedule?.root && (
				<p className="form-error">{errors.schedule.root.message}</p>
			)}
			{errors.schedule?.message && (
				<p className="form-error">{errors.schedule.message}</p>
			)}
		</div>
	);
}
